import React, { useCallback } from 'react';
import {
  UpdateCalculationConfigurationPayload,
  useGetCalculationConfigurationQuery,
  useUpdateCalculationConfigurationMutation,
} from '../../../../apis/apiGlobalConfiguration';
import {
  ConfigurationFieldStatus,
  rtkQueryPatchStatusToConfigurationFieldStatus,
} from '@ydistri/utils';
import { useResetBrandConfigurationMutation } from '../apiBrands';
import useIsConfigurationEditable from '../../../../hooks/useIsConfigurationEditable';
import { useTemplateOrCalculationWithValue } from '../../../../hooks/useTemplateOrCalculationWithValue';
import { skipToken } from '@reduxjs/toolkit/query';
import TypeOfExceptionButtons from '../../components/TypeOfExceptionButtons';

interface BrandTypeOfExceptionProps {
  hasException: boolean;
}

const BrandTypeOfException = ({ hasException }: BrandTypeOfExceptionProps): React.ReactElement => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const isConfigurationEditable = useIsConfigurationEditable();

  const { data: calculationConfiguration, isFetching } = useGetCalculationConfigurationQuery(
    templateOrCalculation.value,
  );
  const allEnabled = calculationConfiguration?.brandsAllEnabled;

  const [updateConfiguration, updateConfigurationStatus] =
    useUpdateCalculationConfigurationMutation();
  const [resetConfiguration] = useResetBrandConfigurationMutation();

  const status = isFetching
    ? ConfigurationFieldStatus.LOADING
    : rtkQueryPatchStatusToConfigurationFieldStatus(updateConfigurationStatus);

  const onChangeHandler = useCallback(
    (value: boolean) => {
      if (templateOrCalculation.hasValidValue && templateOrCalculation.value !== skipToken) {
        const payload: UpdateCalculationConfigurationPayload = {
          templateOrCalculation: templateOrCalculation.value,
          request: {
            brandsAllEnabled: value,
          },
        };
        resetConfiguration(templateOrCalculation);
        updateConfiguration(payload);
      }
    },
    [resetConfiguration, templateOrCalculation, updateConfiguration],
  );

  const onSetAllEnabled = useCallback(() => onChangeHandler(true), [onChangeHandler]);
  const onSetAllDisabled = useCallback(() => onChangeHandler(false), [onChangeHandler]);

  return (
    <TypeOfExceptionButtons
      allEnabled={allEnabled}
      status={status}
      disabled={!(isConfigurationEditable && templateOrCalculation.hasValidValue)}
      templateOrCalculationType={templateOrCalculation.type}
      hasException={hasException}
      onSetAllEnabled={onSetAllEnabled}
      onSetAllDisabled={onSetAllDisabled}
      confirmationTitle="Brands"
    />
  );
};

export default BrandTypeOfException;
